import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useUser } from '@/context/UserContext';
import EnrollModal from '@/components/EnrollModal';
import { 
  BookOpen, 
  Languages, 
  Calculator, 
  History, 
  Palette, 
  ChevronRight,
  Trophy,
  Users,
  Zap,
  GraduationCap
} from 'lucide-react';

const COURSES = [
  {
    id: 'memeglish',
    name: 'MEMEGLISH',
    subtitle: 'Vocabulary & Terminology',
    description: 'Master the language of the trenches. From "ape" to "rug", speak fluent degen.',
    icon: Languages,
    color: 'pump-green',
    gradient: 'from-pump-green/20 to-transparent'
  },
  {
    id: 'memethematics',
    name: 'MEMETHEMATICS',
    subtitle: 'Numbers & Analytics',
    description: 'Learn to read charts, understand market cap, and analyze like a pro.',
    icon: Calculator,
    color: 'degen-purple',
    gradient: 'from-degen-purple/20 to-transparent'
  },
  {
    id: 'memestory',
    name: 'MEMESTORY',
    subtitle: 'KOL History & Legends',
    description: 'Study the legends who shaped pump.fun. Their stories, their strategies.',
    icon: History,
    color: 'yellow-500',
    gradient: 'from-yellow-500/20 to-transparent'
  },
  {
    id: 'memeart',
    name: 'MEMEART',
    subtitle: 'NFT History & Creation',
    description: 'Explore the top NFTs of 2020-2021 and learn how they work.',
    icon: Palette,
    color: 'cyan-400',
    gradient: 'from-cyan-400/20 to-transparent'
  }
];

const HomePage = () => {
  const { user } = useUser();
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const { scrollYProgress } = useScroll();
  
  // Optimized parallax - using scrollYProgress (0-1) and GPU-accelerated transforms
  const blob1Y = useTransform(scrollYProgress, [0, 1], ['0%', '-15%']);
  const blob2Y = useTransform(scrollYProgress, [0, 1], ['0%', '-10%']);
  const blob3Y = useTransform(scrollYProgress, [0, 1], ['0%', '-20%']);

  const getProgress = (courseId) => {
    if (!user?.progress?.[courseId]) return { completed: false, quiz_passed: false };
    return user.progress[courseId];
  };

  const completedCourses = user ? COURSES.filter(c => getProgress(c.id).quiz_passed).length : 0;

  return (
    <div className="min-h-screen relative overflow-hidden pencil-cursor">
      {/* Optimized Purple Background - GPU accelerated with will-change */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ willChange: 'transform', contain: 'paint' }}>
        {/* Large purple blob - top right - using translateZ(0) for GPU layer */}
        <motion.div
          style={{ y: blob1Y, translateZ: 0 }}
          className="absolute -top-20 -right-20 w-[450px] h-[450px] rounded-full"
        >
          <div 
            className="w-full h-full rounded-full"
            style={{ 
              background: 'radial-gradient(circle at center, rgba(157,0,255,0.25) 0%, rgba(157,0,255,0.08) 40%, transparent 65%)',
              filter: 'blur(50px)',
              transform: 'translate3d(0,0,0)',
              backfaceVisibility: 'hidden'
            }} 
          />
        </motion.div>
        
        {/* Medium purple blob - left side */}
        <motion.div
          style={{ y: blob2Y, translateZ: 0 }}
          className="absolute top-[35%] -left-20 w-[350px] h-[350px] rounded-full"
        >
          <div 
            className="w-full h-full rounded-full"
            style={{ 
              background: 'radial-gradient(circle at center, rgba(139,92,246,0.2) 0%, rgba(157,0,255,0.06) 45%, transparent 65%)',
              filter: 'blur(45px)',
              transform: 'translate3d(0,0,0)',
              backfaceVisibility: 'hidden'
            }} 
          />
        </motion.div>
        
        {/* Small purple orb - bottom right */}
        <motion.div
          style={{ y: blob3Y, translateZ: 0 }}
          className="absolute bottom-[25%] right-[12%] w-[200px] h-[200px] rounded-full"
        >
          <div 
            className="w-full h-full rounded-full"
            style={{ 
              background: 'radial-gradient(circle at center, rgba(124,58,237,0.3) 0%, rgba(157,0,255,0.1) 40%, transparent 60%)',
              filter: 'blur(35px)',
              transform: 'translate3d(0,0,0)',
              backfaceVisibility: 'hidden'
            }} 
          />
        </motion.div>
        
        {/* Static ambient glow - no animation for max performance */}
        <div 
          className="absolute top-[55%] left-[25%] w-[250px] h-[250px] rounded-full"
          style={{ 
            background: 'radial-gradient(circle at center, rgba(157,0,255,0.12) 0%, transparent 55%)',
            filter: 'blur(35px)',
            transform: 'translate3d(0,0,0)'
          }} 
        />
      </div>
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-32">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-pump-green/5 via-transparent to-transparent" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pump-green/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-degen-purple/10 rounded-full blur-[128px]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-pump-green/30"
            >
              <GraduationCap size={16} className="text-pump-green" />
              <span className="text-sm font-medium text-zinc-300">The Ivy League for Degens</span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-unbounded font-black text-5xl md:text-7xl lg:text-8xl text-white tracking-tight"
              data-testid="hero-title"
            >
              PUMP
              <span className="text-pump-green neon-text"> UNIVERSITY</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl text-zinc-400 font-jakarta max-w-2xl mx-auto"
            >
              Earn your <span className="text-pump-green font-semibold">Bachelor of Pumpology</span>.
              <br />
              Master the art of memecoins in 4 legendary courses.
            </motion.p>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              {user ? (
                <Link
                  to="/classes"
                  data-testid="continue-learning-btn"
                  className="px-8 py-4 bg-pump-green text-black font-bold text-lg rounded-full hover:shadow-neon-strong transition-all duration-300 uppercase tracking-wider flex items-center gap-2"
                >
                  Continue Learning
                  <ChevronRight size={20} />
                </Link>
              ) : (
                <button
                  onClick={() => setShowEnrollModal(true)}
                  data-testid="hero-enroll-btn"
                  className="px-8 py-4 bg-pump-green text-black font-bold text-lg rounded-full hover:shadow-neon-strong transition-all duration-300 uppercase tracking-wider flex items-center gap-2"
                >
                  Enroll Now
                  <ChevronRight size={20} />
                </button>
              )}
              <Link
                to="/classes"
                className="px-8 py-4 border border-white/20 text-white font-medium rounded-full hover:bg-white/5 transition-all"
              >
                View Curriculum
              </Link>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex items-center justify-center gap-8 md:gap-16 pt-8"
            >
              <div className="text-center">
                <div className="font-unbounded font-bold text-3xl text-white">4</div>
                <div className="text-sm text-zinc-500">Courses</div>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div className="text-center">
                <div className="font-unbounded font-bold text-3xl text-pump-green">50+</div>
                <div className="text-sm text-zinc-500">Terms & Concepts</div>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div className="text-center">
                <div className="font-unbounded font-bold text-3xl text-degen-purple">1</div>
                <div className="text-sm text-zinc-500">NFT Degree</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Progress Section (if user enrolled) */}
      {user && (
        <section className="py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="glass rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-pump-green/20 flex items-center justify-center">
                  <Trophy className="text-pump-green" size={24} />
                </div>
                <div>
                  <h3 className="font-unbounded font-bold text-white">Your Progress</h3>
                  <p className="text-zinc-400 text-sm">{completedCourses} of 4 courses completed</p>
                </div>
              </div>
              <div className="w-full md:w-64">
                <div className="h-3 bg-pump-surface rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-pump-green rounded-full shadow-[0_0_10px_#39FF14] transition-all duration-500"
                    style={{ width: `${(completedCourses / 4) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Courses Section */}
      <section className="py-16 px-4" data-testid="courses-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-unbounded font-bold text-3xl md:text-4xl text-white mb-4">
              THE <span className="text-pump-green">CURRICULUM</span>
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              Four essential courses to transform you from a noob to a certified degen
            </p>
          </motion.div>

          {/* Course Grid - Bento Style */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {COURSES.map((course, index) => {
              const progress = getProgress(course.id);
              const isCompleted = progress.quiz_passed;
              const isLocked = index > 0 && !getProgress(COURSES[index - 1].id).quiz_passed && user;
              
              return (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={isLocked ? '#' : `/course/${course.id}`}
                    data-testid={`course-card-${course.id}`}
                    className={`block group relative overflow-hidden rounded-2xl glass p-8 transition-all duration-300 ${
                      isLocked 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:scale-[1.02] hover:border-pump-green/50 hover:shadow-neon'
                    }`}
                  >
                    {/* Background Gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${course.gradient} opacity-0 group-hover:opacity-100 transition-opacity`} />
                    
                    {/* Completed Badge */}
                    {isCompleted && (
                      <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-pump-green/20 border border-pump-green/30 text-pump-green text-xs font-bold">
                        COMPLETED
                      </div>
                    )}

                    {/* Locked Badge */}
                    {isLocked && (
                      <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs font-bold">
                        LOCKED
                      </div>
                    )}
                    
                    <div className="relative flex items-start gap-6">
                      <div className={`w-16 h-16 rounded-xl bg-${course.color}/20 flex items-center justify-center shrink-0`}>
                        <course.icon className={`text-${course.color}`} size={28} />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="font-unbounded font-bold text-xl text-white mb-1 group-hover:text-pump-green transition-colors">
                          {course.name}
                        </h3>
                        <p className="text-sm text-zinc-500 mb-3">{course.subtitle}</p>
                        <p className="text-zinc-400 text-sm leading-relaxed">
                          {course.description}
                        </p>
                      </div>
                      
                      <ChevronRight className="text-zinc-600 group-hover:text-pump-green group-hover:translate-x-1 transition-all" size={24} />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-transparent via-pump-surface/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="glass rounded-2xl p-8 text-center">
              <div className="w-14 h-14 rounded-xl bg-pump-green/20 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="text-pump-green" size={24} />
              </div>
              <h3 className="font-unbounded font-bold text-white mb-2">Interactive Slides</h3>
              <p className="text-zinc-400 text-sm">Learn through engaging, visual content designed for degens</p>
            </div>
            
            <div className="glass rounded-2xl p-8 text-center">
              <div className="w-14 h-14 rounded-xl bg-degen-purple/20 flex items-center justify-center mx-auto mb-4">
                <Zap className="text-degen-purple" size={24} />
              </div>
              <h3 className="font-unbounded font-bold text-white mb-2">AI Advisor</h3>
              <p className="text-zinc-400 text-sm">24/7 Memeademic Advisor to answer all your questions</p>
            </div>
            
            <div className="glass rounded-2xl p-8 text-center">
              <div className="w-14 h-14 rounded-xl bg-cyan-400/20 flex items-center justify-center mx-auto mb-4">
                <Trophy className="text-cyan-400" size={24} />
              </div>
              <h3 className="font-unbounded font-bold text-white mb-2">NFT Certificate</h3>
              <p className="text-zinc-400 text-sm">Earn your Bachelor of Pumpology NFT upon completion</p>
            </div>
          </div>
        </div>
      </section>

      <EnrollModal open={showEnrollModal} onClose={() => setShowEnrollModal(false)} />
    </div>
  );
};

export default HomePage;
